import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { useBoardName } from "../../../reactQuery/boards";
import { useLoginUser } from "../../../reactQuery/users";
import { user } from "../../../redux/usersSlice";
import { useResponsive } from "../../../hooks/useResponsive";
import { useCards } from "../../../reactQuery/cards";
import { currentUserId } from "../../../utils/constants";
import useBoardIdByKey from "../../../hooks/useBoardIdByKey";
import { getResponseData } from "../../../reactQuery/response";
import { useBoardPermissions } from "./useBoardPermissions";
import { useBoardViewParams } from "./useBoardViewParams";
import { useBoardCardNavigation } from "./useBoardCardNavigation";
import { useMutation } from "@tanstack/react-query";
import { updateLastSeenOfUser } from "../../../services/apiBoards";

export function useBoardPageState(showCardPopup) {
  const userId = currentUserId();
  const dispatch = useDispatch();
  const { id: routeBoardId, cardId } = useParams();
  const shouldShowCardPopup = showCardPopup || Boolean(cardId);
  const [loading, setLoading] = useState(false);
  const gridView = useSelector((state) => state.search.gridView);
  const isMobile = useResponsive("down", "md");

  const {
    id: boardId,
    board: boardLookup,
    isLoading: isBoardLookupLoading,
  } = useBoardIdByKey();
  const { data: loginUserResponse, isLoading: isLoginUserLoading } =
    useLoginUser(userId);
  const {
    boardName: boardQuery,
    error: boardError,
    isLoading: isBoardLoading,
  } = useBoardName(boardId);
  const {
    cards: cardsQuery,
    isLoading: isCardsLoading,
  } = useCards(boardId);

  const board = getResponseData(boardQuery, boardLookup);
  const cards = getResponseData(cardsQuery, []);
  const loginUser = getResponseData(loginUserResponse);

  useEffect(() => {
    if (loginUser) {
      dispatch(user(loginUser));
    }
  }, [dispatch, loginUser]);


  const { mutate } = useMutation({
    mutationFn: updateLastSeenOfUser
  });

  useEffect(()=>{
    boardId  && mutate(boardId);
  },[boardId])

  const viewState = useBoardViewParams();
  const permissionState = useBoardPermissions(board, boardError);
  const navigationState = useBoardCardNavigation({
    cards,
    routeCardId: cardId,
    enabled: shouldShowCardPopup,
  });

  const listCards = useMemo(() => {
    return cards
      .filter((item) => {
        const isNotParentCard = !item?.card?.parent_card_id;

        if (!viewState.includeQuickTasks) {
          return isNotParentCard && !item?.card?.is_todo;
        }

        return isNotParentCard;
      })
      .sort((cardA, cardB) => {
        if (cardA?.card?.is_todo && !cardB?.card?.is_todo) {
          return -1;
        }

        if (!cardA?.card?.is_todo && cardB?.card?.is_todo) {
          return 1;
        }

        return 0;
      });
  }, [cards, viewState.includeQuickTasks]);

  const contentOffsetClass = useMemo(() => {
    if (isMobile) {
      return "mt-0";
    }

    return viewState.isMilestoneView ? "mt-185" : "mt-115";
  }, [isMobile, viewState.isMilestoneView]);
  

  return {
    board,
    boardId,
    boardError,
    cards,
    cardId: navigationState.activeCardId,
    cardsOnBoard: navigationState.cardsOnBoard,
    contentOffsetClass,
    gridView,
    isCardsLoading,
    isLoading: isLoginUserLoading || isBoardLoading ||isBoardLookupLoading,
    loading,
    listCards,
    permissionState,
    setLoading,
    viewState,
  };
}
